/**
 * Utility functions for tracking and analytics
 */

import { getCheckoutSession } from 'API/payment';

declare global {
  interface Window {
    dataLayer: any[];
    appUserId?: string;
    appUserEmail?: string;
  }
}

/**
 * Map product IDs to human-readable display names for tracking.
 * Only the 4 current products are mapped; unknown IDs are returned as-is.
 */
function getProductDisplayName(productId?: string): string | null {
  if (!productId) return null;

  const productNames: { [key: string]: string } = {
    'saia_recruiting': 'SAIA Recruiting',
    'saia_sales': 'SAIA Sales',
    'motive': 'Motive',
    'lead': 'Lead',
  };

  return productNames[productId] || productId;
}

/**
 * Hash email using SHA-256 for Enhanced Conversions
 */
async function hashEmailAsync(email: string): Promise<string> {
  const enc = new TextEncoder();
  const digest = await crypto.subtle.digest('SHA-256', enc.encode(email.trim().toLowerCase()));
  return Array.from(new Uint8Array(digest)).map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Track registration completion event
 * @param userId - Optional user ID
 * @param email - Optional user email
 */
export async function trackRegistrationComplete(userId?: string, email?: string): Promise<void> {
  try {
    // Initialize dataLayer if it doesn't exist
    window.dataLayer = window.dataLayer || [];

    // Set global user data for potential use by other scripts
    if (userId) {
      window.appUserId = userId;
    }
    if (email) {
      window.appUserEmail = email;
    }

    // Hash email if available and crypto is supported
    let email_hashed: string | undefined = undefined;
    try {
      if (email && window.crypto && crypto.subtle) {
        email_hashed = await hashEmailAsync(email);
      }
    } catch (e) {
      console.warn('Failed to hash email for tracking:', e);
    }

    // Push registration event to dataLayer
    window.dataLayer.push({
      event: 'registration_complete',
      user_id: userId || undefined,
      email_hashed: email_hashed || undefined
    });

    console.log('Registration tracking event sent:', {
      event: 'registration_complete',
      user_id: userId || undefined,
      email_hashed: email_hashed ? '[HASHED]' : undefined
    });

  } catch (error) {
    console.error('Failed to track registration completion:', error);
  }
}

/**
 * Interface for checkout session details
 */
interface CheckoutSessionDetails {
  amount_total?: number;
  currency?: string;
  payment_intent?: string;
}

/**
 * Track successful payment/purchase event
 * @param sessionId - Stripe session ID from URL
 * @param productId - The specific product ID from the Products enum
 * @param actualValue - Actual product value in currency units (from pricing data)
 * @param actualCurrency - Actual currency (from pricing data)
 * @param fallbackValue - Fallback value in cents (default: 120000 = 1200 EUR)
 * @param fallbackCurrency - Fallback currency (default: 'EUR')
 */
export async function trackPurchaseComplete(
  sessionId?: string | null,
  productId?: string,
  actualValue?: number,
  actualCurrency?: string,
  fallbackValue: number = 120000, // 1200 EUR in cents
  fallbackCurrency: string = 'EUR'
): Promise<void> {
  try {
    // Initialize dataLayer if it doesn't exist
    window.dataLayer = window.dataLayer || [];

    // 1) Attempt to fetch checkout session details from server
    let details: CheckoutSessionDetails | null = null;
    if (sessionId) {
      try {
        const response = await getCheckoutSession(sessionId);
        if (!response.error && response.data) {
          details = response.data;
        }
      } catch (e) {
        console.warn('Failed to fetch checkout session details, using fallback values:', e);
      }
    }

    // 2) Calculate final values with priority order: Stripe session > actual pricing data > fallbacks
    let value: number;
    let currency: string;

    if (details && typeof details.amount_total === 'number') {
      // Use Stripe session data if available (most accurate)
      value = details.amount_total / 100; // Convert from cents to currency units
      currency = (details.currency || fallbackCurrency).toUpperCase();
    } else if (actualValue && actualCurrency) {
      // Use actual pricing data passed from the application
      value = actualValue;
      currency = actualCurrency.toUpperCase();
    } else {
      // Use fallback values
      value = fallbackValue / 100;
      currency = fallbackCurrency.toUpperCase();
    }

    // 3) Determine product name from product ID or use default
    const productName = getProductDisplayName(productId) || 'SAIA Product';

    // 4) Determine transaction ID
    const transactionId = sessionId || (details && details.payment_intent) || undefined;

    // 5) Push purchase event to dataLayer
    window.dataLayer.push({
      event: 'purchase',
      value: value,
      currency: currency,
      transaction_id: transactionId,
      items: [{
        item_name: productName,
        item_id: productId || undefined,
        price: value,
        quantity: 1
      }]
    });

    console.log('Purchase tracking event sent:', {
      event: 'purchase',
      value: value,
      currency: currency,
      transaction_id: transactionId || '[NONE]',
      items: [{
        item_name: productName,
        price: value,
        quantity: 1
      }]
    });

  } catch (error) {
    console.error('Failed to track purchase completion:', error);
  }
}
